import { JSX, SyntheticEvent, useCallback, useMemo } from "react";

import Autocomplete, {
	AutocompleteRenderInputParams
} from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import { asTitle } from "utils/Utils";

export type ComboBoxProps<T> = {
	label?: string;
	required?: boolean;
	disabled?: boolean;
	error?: boolean;
	options: Array<T>;
	value?: T | null;

	getOptionLabel: (option: T) => string;
	getOptionKey: (option: T) => string | number;

	onChange: (option: T | null) => void;
};

export default function ComboBox<T>(
	props: Readonly<ComboBoxProps<T>>
): JSX.Element {
	const renderInput = useCallback(
		(params: AutocompleteRenderInputParams) => (
			<TextField
				{...params}
				variant="standard"
				required={props.required}
				error={props.error}
				label={props.label ? asTitle(props.label) : undefined}
			/>
		),
		[props.required, props.error, props.label]
	);

	const handleChange = useCallback(
		(_event: SyntheticEvent, value: T | null) => {
			props.onChange(value);
		},
		[props.onChange]
	);

	const sortedOptions: T[] = useMemo(
		() =>
			props.options.toSorted((a, b) =>
				props.getOptionLabel(a).localeCompare(props.getOptionLabel(b))
			),
		[props.options, props.getOptionLabel]
	);

	return (
		<Autocomplete
			disabled={props.disabled}
			value={props.value}
			options={sortedOptions}
			getOptionLabel={props.getOptionLabel}
			getOptionKey={props.getOptionKey}
			renderInput={renderInput}
			onChange={handleChange}
		/>
	);
}
